import React, { useState, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  DocumentPlusIcon,
  ClockIcon,
  ChartBarIcon,
  AdjustmentsHorizontalIcon,
  Cog6ToothIcon,
  BeakerIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { ActiveTab } from '../types';
import { formatFecha } from '../utils/fecha';
import { getCurrentUser, getUserType, hasPermission, hasUserManagementPermission, hasTestManagementPermission } from '../services/authService';
import { getDashboardStats, getProximosVencimientos } from '../services/api';
import { Card, LoadingState } from './ui';

interface DashboardProps {
  onNavigate: (tab: ActiveTab) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ onNavigate }) => {
  const currentUser = getCurrentUser();
  const userType = getUserType();
  const [stats, setStats] = useState({ solicitados: 0, pendientes: 0, enProceso: 0, completados: 0 });
  const [proximosVencimientos, setProximosVencimientos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cargarDatos = async () => {
      try {
        setLoading(true);
        if (canSearch() || canEdit() || hasPermission('p_carga') || hasPermission('p_nuevo_analisis')) {
          setStats(await getDashboardStats());
        }

        if (userType === 'laboratorio') {
          const vencimientos = await getProximosVencimientos();
          setProximosVencimientos(vencimientos);
        }
      } catch (error) {
        console.error('Error al cargar datos del dashboard:', error);
      } finally {
        setLoading(false);
      }
    };

    cargarDatos();
  }, [userType]);

  const canSearch = () => hasPermission('p_busqueda');
  const canEdit = () => hasPermission('p_edicion');

  // Sólo se muestran indicadores de análisis a quien tiene acceso a los
  // análisis. Un administrador que únicamente gestiona usuarios no los ve.
  const veAnalisis = () =>
    canSearch() || canEdit() || hasPermission('p_carga') || hasPermission('p_nuevo_analisis');

  const getStats = () => {
    if (!veAnalisis()) return [];
    return [
      { label: 'Análisis activos', value: stats.solicitados, icon: BeakerIcon, tone: 'brand' as const },
      { label: 'Pendientes de revisión', value: stats.pendientes + stats.enProceso, icon: ClockIcon, tone: 'warning' as const },
      { label: 'Completados', value: stats.completados, icon: CheckCircleIcon, tone: 'success' as const },
    ];
  };

  const TONE_CLASSES: Record<string, string> = {
    brand: 'bg-brand-50 text-brand-700',
    warning: 'bg-amber-50 text-amber-600',
    danger: 'bg-red-50 text-red-600',
    success: 'bg-green-50 text-green-600',
  };

  const getQuickLinks = () => {
    const links: { name: string; icon: React.ComponentType<{ className?: string }>; action: ActiveTab }[] = [];

    if (canSearch()) links.push({ name: 'Buscar', icon: MagnifyingGlassIcon, action: 'busqueda' });
    // Hace falta el permiso de creación, no alcanza con ser administrador; y un
    // laboratorio recibe análisis, no los genera.
    if (hasPermission('p_nuevo_analisis') && userType !== 'laboratorio') {
      links.push({ name: 'Nuevo análisis', icon: DocumentPlusIcon, action: 'nuevo-analisis' });
    }

    if (userType === 'laboratorio') {
      links.push({ name: 'Análisis pendientes', icon: ClockIcon, action: 'analisis-pendientes' });
      links.push({ name: 'Análisis', icon: ChartBarIcon, action: 'mis-analisis' });
    }

    if (userType === 'cliente') {
      links.push({ name: 'Análisis', icon: ChartBarIcon, action: 'mis-analisis' });
    }

    if (hasUserManagementPermission() || hasTestManagementPermission()) {
      links.push({ name: 'Gestión', icon: AdjustmentsHorizontalIcon, action: 'gestion' });
    }

    links.push({ name: 'Configuración', icon: Cog6ToothIcon, action: 'configuracion' });

    return links;
  };

  const dashboardStats = getStats();
  const quickLinks = getQuickLinks();

  if (loading) {
    return <LoadingState label="Cargando panel..." />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Panel de control</h1>
        <p className="text-sm text-slate-500 mt-1">
          Bienvenido/a, {currentUser?.nombre} {currentUser?.apellido}
        </p>
      </div>

      {dashboardStats.length > 0 && (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {dashboardStats.map((stat) => (
          <Card key={stat.label} className="flex items-center gap-4">
            <div className={`flex items-center justify-center h-11 w-11 rounded-lg flex-shrink-0 ${TONE_CLASSES[stat.tone]}`}>
              <stat.icon className="h-6 w-6" />
            </div>
            <div>
              <p className="text-sm text-slate-500">{stat.label}</p>
              <p className="text-2xl font-semibold text-slate-900">{stat.value}</p>
            </div>
          </Card>
        ))}
      </div>
      )}

      <Card>
        <CardTitle>Información de usuario</CardTitle>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <InfoField label="Nombre" value={`${currentUser?.nombre ?? ''} ${currentUser?.apellido ?? ''}`.trim()} />
          <InfoField label="Email" value={currentUser?.email} />
          <InfoField label="Tipo de usuario" value={userType || 'No definido'} capitalize />
        </div>

        {currentUser?.info_adicional && (
          <div className="mt-4 pt-4 border-t border-slate-200">
            {userType === 'cliente' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoField label="Empresa" value={currentUser.info_adicional.nombre} />
                <InfoField label="Contacto" value={currentUser.info_adicional.telefono} />
              </div>
            )}
            {userType === 'laboratorio' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoField label="Laboratorio" value={currentUser.info_adicional.nombre} />
                <InfoField label="Especialidad" value={currentUser.info_adicional.especialidad} />
              </div>
            )}
            {userType === 'administrador' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoField label="Rol" value={currentUser.info_adicional.rol} />
                <InfoField label="Permisos" value={currentUser.info_adicional.nivel_acceso || 'Estándar'} />
              </div>
            )}
          </div>
        )}
      </Card>

      <div>
        <CardTitle>Acciones rápidas</CardTitle>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {quickLinks.map((link) => (
            <button
              key={link.name}
              onClick={() => onNavigate(link.action)}
              className="flex flex-col items-center justify-center gap-2 h-28 rounded-lg border border-slate-200 bg-white
                hover:border-brand-300 hover:bg-brand-50/40 hover:shadow-card transition-all"
            >
              <link.icon className="h-6 w-6 text-brand-700" />
              <span className="text-sm font-medium text-slate-700 text-center px-1">{link.name}</span>
            </button>
          ))}
        </div>
      </div>

      {userType === 'laboratorio' && (
        <Card>
          <CardTitle>Próximos vencimientos</CardTitle>
          {proximosVencimientos.length > 0 ? (
            <div className="space-y-3">
              {proximosVencimientos.map((analisis, index) => {
                const fechaSolicitud = new Date(analisis.fecha_solicitud);
                const diasTranscurridos = Math.floor((new Date().getTime() - fechaSolicitud.getTime()) / (1000 * 60 * 60 * 24));

                return (
                  <div key={index} className="flex justify-between items-start pb-3 border-b border-slate-100 last:border-b-0 last:pb-0">
                    <div>
                      <p className="text-sm font-medium text-slate-800">Análisis #{analisis.id_a_nea || `A${analisis.id}`}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Solicitado: {formatFecha(analisis.fecha_solicitud)} · Estado:{' '}
                        {analisis.estado === 0 ? 'Pendiente' : analisis.estado === 1 ? 'En proceso' : 'Finalizado'}
                      </p>
                    </div>
                    <span className={`text-xs font-medium ${diasTranscurridos > 10 ? 'text-red-600' : 'text-amber-600'}`}>
                      {diasTranscurridos > 10 ? 'Crítico' : `${diasTranscurridos} días`}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
              <ClipboardDocumentListIcon className="h-5 w-5" />
              No hay análisis próximos a vencer
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

const CardTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="text-base font-semibold text-slate-900 mb-4">{children}</h3>
);

const InfoField: React.FC<{ label: string; value?: string; capitalize?: boolean }> = ({ label, value, capitalize }) => (
  <div>
    <p className="text-xs text-slate-400">{label}</p>
    <p className={`text-sm font-medium text-slate-800 ${capitalize ? 'capitalize' : ''}`}>{value || 'N/A'}</p>
  </div>
);

export default Dashboard;
